/**
 * Far & Away bundles landing page (sections/fa-bundles-*). No dependencies.
 *
 * <fa-bundles-carousel>  A sideways row on native scrolling and snapping (swipe on touch
 *   screens). [data-track] is the row; [data-prev] / [data-next] step one slide and turn off
 *   at the ends; [data-dot="n"] buttons (dots or thumbnails) jump to a slide and carry
 *   aria-current. refresh() after the slides change.
 *
 * <fa-bundles-buybox>  The buy box (sections/fa-bundles-bundle). A set card re-renders the
 *   section for that set's product URL (Section Rendering API) and swaps the section for it,
 *   keeping the colour on show when the set has it. A colour change runs through Horizon's variant picker
 *   as on the product page. Adding to cart is the product page's own blocks and flow.
 *
 * <fa-bundles-build>  "Build Your Own Bundle" (sections/fa-bundles-byo). Each card
 *   (<fa-bundles-build-card>) switches its photo, prices and the variant its Add to cart posts
 *   when a colour dot is pressed. Cards open in the buy box's colour and follow it until the
 *   shopper picks a colour on that card.
 *
 * Sticky add to cart: the product page's bar (snippets/fa-bundles-sticky) runs on the theme's
 *   own script; this file only corrects it after jumps that skip past the buy box.
 *
 * <fa-bundles-loop>  A decorative muted loop: fetched and started only when it comes near the
 *   screen, paused when it leaves; never starts by itself under reduced motion.
 *
 * Links with data-fa-bundles-scroll to an anchor on the page scroll there smoothly (or jump,
 * under reduced motion) and move keyboard focus to the target. The makers film's play button
 * ([data-fa-video-start]) loads the theme's <fa-video-player> (assets/fa-video.js).
 */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isPhone = () => window.matchMedia('(max-width: 749px)').matches;

/* ---------------------------------------------------------------- Carousel */

class FaBundlesCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const dot = target?.closest('[data-dot]');
      if (dot && this.contains(dot)) {
        this.go(Number(dot.getAttribute('data-dot')));
        return;
      }
      if (target?.closest('[data-prev]')) this.go(this.index() - 1);
      else if (target?.closest('[data-next]')) this.go(this.index() + 1);
    };
    this.addEventListener('click', this.onClick);

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  get slides() {
    return /** @type {HTMLElement[]} */ ([...(this.track?.children || [])]);
  }

  /** A slide's scroll position inside the row */
  position(slide) {
    const first = this.slides[0];
    return first ? slide.offsetLeft - first.offsetLeft : 0;
  }

  /** The slide nearest the row's start edge */
  index() {
    const left = this.track.scrollLeft;
    let best = 0;
    let distance = Infinity;
    this.slides.forEach((slide, i) => {
      const d = Math.abs(this.position(slide) - left);
      if (d < distance) {
        distance = d;
        best = i;
      }
    });
    return best;
  }

  /**
   * @param {number} i
   * @param {boolean} [instant]
   */
  go(i, instant = false) {
    const slides = this.slides;
    if (!slides.length) return;
    const target = slides[Math.max(0, Math.min(i, slides.length - 1))];
    this.track.scrollTo({ left: this.position(target), behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
  }

  refresh() {
    this.update();
  }

  update() {
    const { track } = this;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const scrollable = max > 2 && getComputedStyle(track).overflowX !== 'visible';

    // A row that scrolls is reachable by keyboard (arrow keys scroll it)
    if (scrollable) track.setAttribute('tabindex', '0');
    else track.removeAttribute('tabindex');

    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (prev) prev.disabled = !scrollable || track.scrollLeft <= 2;
    if (next) next.disabled = !scrollable || track.scrollLeft >= max - 2;

    // At the end of a row that shows several slides, the last one is the current one
    const current = scrollable && track.scrollLeft >= max - 2 ? this.slides.length - 1 : this.index();
    const dots = this.querySelectorAll('[data-dot]');
    dots.forEach((dot, i) => dot.setAttribute('aria-current', i === current ? 'true' : 'false'));

    // Keep the current thumbnail in its own row's view, without moving the page
    const thumbs = this.querySelector('[data-thumbs]');
    const active = dots[current];
    if (thumbs && active instanceof HTMLElement && thumbs.contains(active)) {
      const start = active.offsetLeft - thumbs.offsetLeft;
      const end = start + active.offsetWidth;
      if (start < thumbs.scrollLeft) thumbs.scrollLeft = start - 4;
      else if (end > thumbs.scrollLeft + thumbs.clientWidth) thumbs.scrollLeft = end - thumbs.clientWidth + 4;
    }
  }
}

if (!customElements.get('fa-bundles-carousel')) customElements.define('fa-bundles-carousel', FaBundlesCarousel);

/* ---------------------------------------------------------------- Buy box */

/** The colour chosen in the buy box, or '' */
const buyboxColour = () => {
  const input = document.querySelector('fa-bundles-buybox variant-picker fieldset input:checked');
  return input instanceof HTMLInputElement ? input.value : '';
};

/** Tells "Build Your Own Bundle" which colour the buy box shows */
const announceColour = (colour) => {
  if (colour) document.dispatchEvent(new CustomEvent('fa-bundles:colour', { detail: { colour } }));
};

class FaBundlesBuybox extends HTMLElement {
  connectedCallback() {
    this.onChange = (event) => {
      const input = event.target;
      if (input instanceof HTMLInputElement && input.name.startsWith('FaBundlesSet-')) this.loadSet(input);
    };
    this.addEventListener('change', this.onChange);

    // A colour change runs as on the product page: Horizon's variant picker re-renders itself
    // and the price, photos, form and other blocks update from the response. Inside
    // <featured-product-information> the picker would instead morph the whole buy box (photo
    // zoom included), so its change event is taken here first and handed to the picker's own
    // methods without that whole-box morph.
    this.onPickerChange = (event) => {
      const input = event.target;
      const picker = input instanceof HTMLInputElement ? input.closest('variant-picker') : null;
      if (!picker || !this.contains(picker) || typeof picker.fetchUpdatedSection !== 'function') return;
      event.stopPropagation();
      picker.updateSelectedOption(input);
      const { optionValueId = '', variantId = '', connectedProductUrl = '' } = input.dataset;
      picker.fetchUpdatedSection(picker.buildRequestUrl(input), { detail: { optionValueId, variantId, connectedProductUrl } });
      announceColour(input.value);
    };
    this.addEventListener('change', this.onPickerChange, { capture: true });

    // The buy box is not this page's product. Horizon's variant picker marks itself as the
    // page's product when the section comes back from a product URL, and would then write
    // ?variant= into the page address: take the mark off whenever it appears.
    this.observer = new MutationObserver(() => this.unmark());
    this.observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-template-product-match'],
    });
    this.unmark();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('change', this.onPickerChange, { capture: true });
    this.observer?.disconnect();
  }

  unmark() {
    this.querySelectorAll('variant-picker[data-template-product-match]').forEach((picker) =>
      picker.removeAttribute('data-template-product-match')
    );
  }

  /**
   * Shows another set: the section rendered for that product's URL (in the colour on show
   * when the set comes in it) replaces the whole section, so every block starts fresh for the
   * new product, as on its product page. The whole section, not just the buy box: several
   * theme blocks listen on their section element and cannot unhook once taken out of it, so
   * old listeners would keep taking parts out of later colour-change responses.
   * @param {HTMLInputElement} input
   */
  async loadSet(input) {
    const current = this.querySelector('featured-product-information');
    const section = this.closest('.shopify-section');
    const base = (input.dataset.url || '').split('?')[0];
    if (!current || !section || !base) return;

    /** @type {Record<string, number>} */
    let colours = {};
    try {
      colours = JSON.parse(input.dataset.colours || '{}');
    } catch (error) {
      colours = {};
    }
    const chosen = this.querySelector('variant-picker fieldset input:checked');
    const valueId = chosen instanceof HTMLInputElement ? colours[chosen.value] : undefined;
    const url = `${base}?section_id=${encodeURIComponent(current.id)}${valueId ? `&option_values=${valueId}` : ''}`;
    const hadFocus = document.activeElement === input;

    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Set request failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
      const next = html.getElementById(section.id);
      if (!next || !next.querySelector('featured-product-information')) throw new Error('No buy box in the response');
      if (controller.signal.aborted) return;

      section.replaceWith(document.adoptNode(next));
      announceColour(buyboxColour());

      if (hadFocus) {
        const again = next.querySelector(`input[name="${CSS.escape(input.name)}"]:checked`);
        if (again instanceof HTMLElement) again.focus({ preventScroll: true });
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error(error);
      // Nothing changed: go to the set's own page instead
      window.location.assign(base);
    } finally {
      if (this.abortController === controller) this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('fa-bundles-buybox')) customElements.define('fa-bundles-buybox', FaBundlesBuybox);

/* ---------------------------------------------------------------- Build Your Own Bundle */

class FaBundlesBuildCard extends HTMLElement {
  connectedCallback() {
    // Pressing a dot can move the row (focus scrolls a half-visible card into view), so the
    // click may land beside the dot it started on: the dot under the pointer when it went down
    // counts for that click.
    this.onPointerDown = (event) => {
      const swatch = event.target instanceof Element ? event.target.closest('.fa-upsells__swatch') : null;
      this.pressed = swatch && this.contains(swatch) ? { swatch, time: Date.now() } : null;
    };
    this.onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      let swatch = target?.closest('.fa-upsells__swatch');
      if (!swatch && this.pressed && Date.now() - this.pressed.time < 1000 && !target?.closest('a, button, input')) {
        swatch = this.pressed.swatch;
      }
      this.pressed = null;
      if (!(swatch instanceof HTMLButtonElement) || !this.contains(swatch) || swatch.disabled) return;
      this.dataset.chosen = '';
      this.select(swatch);
    };
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('click', this.onClick);
  }

  /** @param {string} colour */
  follow(colour) {
    if (this.hasAttribute('data-chosen')) return;
    const swatch = [...this.querySelectorAll('.fa-upsells__swatch')].find(
      (button) => button instanceof HTMLButtonElement && button.dataset.colour === colour && !button.disabled
    );
    if (swatch) this.select(/** @type {HTMLButtonElement} */ (swatch));
  }

  /** @param {HTMLButtonElement} swatch */
  select(swatch) {
    const { variantId, price, compare, perPiece, src, srcset } = swatch.dataset;
    if (!variantId) return;

    this.querySelectorAll('.fa-upsells__swatch').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === swatch));
    });

    const input = this.querySelector('[data-fa-byo-variant]');
    if (input instanceof HTMLInputElement) input.value = variantId;

    this.setText('[data-fa-byo-price]', price);
    this.setText('[data-fa-byo-compare]', compare);
    this.setText('[data-fa-byo-per-piece]', perPiece);

    const img = this.querySelector('[data-fa-byo-image]');
    if (img instanceof HTMLImageElement && src && img.getAttribute('src') !== src) {
      if (srcset) img.srcset = srcset;
      img.src = src;
    }
  }

  /**
   * @param {string} selector
   * @param {string | undefined} value
   */
  setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value ?? '';
  }
}

if (!customElements.get('fa-bundles-build-card')) customElements.define('fa-bundles-build-card', FaBundlesBuildCard);

class FaBundlesBuild extends HTMLElement {
  connectedCallback() {
    this.onColour = (event) => this.follow(event.detail?.colour || '');
    document.addEventListener('fa-bundles:colour', this.onColour);
    this.follow(buyboxColour());
  }

  disconnectedCallback() {
    document.removeEventListener('fa-bundles:colour', this.onColour);
  }

  /** @param {string} colour */
  follow(colour) {
    if (!colour) return;
    this.querySelectorAll('fa-bundles-build-card').forEach((card) => {
      if (card instanceof FaBundlesBuildCard) card.follow(colour);
    });
  }
}

if (!customElements.get('fa-bundles-build')) customElements.define('fa-bundles-build', FaBundlesBuild);

/* ---------------------------------------------------------------- Sticky add to cart */

// The bar under the buy box is the product page's <sticky-add-to-cart> (snippets/fa-bundles-sticky,
// assets/sticky-add-to-cart.js). It shows or hides when the buy box's Add to cart crosses the
// edge of the screen, so it only notices a scroll that has that button on screen at some point.
// Here the buy box sits halfway down a long page: a jump (Home or End, a link to the reviews,
// a fast flick on a phone) can pass the button unseen and leave the bar up over the hero, or
// missing below the buy box. Once a scroll settles, the bar is checked against the button; when
// it is wrong it is swapped for a fresh copy, which starts from where the page is now (the
// component keeps its state in private fields).
if (!window.faBundlesSticky) {
  window.faBundlesSticky = true;
  let timer = 0;
  let lastSwap = 0;

  const check = () => {
    if (!customElements.get('sticky-add-to-cart')) return;
    const section = document.querySelector('fa-bundles-buybox')?.closest('.shopify-section');
    const sticky = section?.querySelector('sticky-add-to-cart');
    const bar = sticky?.querySelector('.sticky-add-to-cart__bar');
    const buttons = section?.querySelector('featured-product-information .buy-buttons-block');
    if (!sticky || !(bar instanceof HTMLElement) || !buttons) return;
    // With Shopify Inbox chat on the page the bar stays hidden, as on the product page
    if (customElements.get('shopify-chat') && document.querySelector('shopify-chat')) return;

    const atc = buttons.getBoundingClientRect();
    const footer = (document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]'))?.getBoundingClientRect();
    const footerNear = !!footer && footer.top < window.innerHeight && footer.bottom > -200;
    const up = bar.dataset.stuck === 'true';
    const wrong = up ? atc.top >= window.innerHeight || footerNear : atc.bottom <= 0 && !footerNear;
    if (!wrong || Date.now() - lastSwap < 500) return;
    lastSwap = Date.now();

    const fresh = /** @type {HTMLElement} */ (sticky.cloneNode(true));
    const freshBar = fresh.querySelector('.sticky-add-to-cart__bar');
    if (freshBar instanceof HTMLElement) {
      freshBar.dataset.stuck = 'false';
      freshBar.inert = true;
    }
    sticky.replaceWith(fresh);
  };

  const later = () => {
    clearTimeout(timer);
    timer = setTimeout(check, 200);
  };
  document.addEventListener('scroll', later, { capture: true, passive: true });
  window.addEventListener('resize', later, { passive: true });
  customElements.whenDefined('sticky-add-to-cart').then(later);
}

/* ---------------------------------------------------------------- Decorative loop */

class FaBundlesLoop extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-fa-bundles-loop-toggle]'));
    if (!this.video) return;

    this.video.addEventListener('playing', () => {
      this.setAttribute('data-playing', '');
      this.toggle?.setAttribute('aria-label', 'Pause video');
    });
    this.video.addEventListener('pause', () => {
      this.removeAttribute('data-playing');
      this.toggle?.setAttribute('aria-label', 'Play video');
    });

    this.toggle?.addEventListener('click', () => {
      if (this.hasAttribute('data-playing')) {
        this.userPaused = true;
        this.video.pause();
      } else {
        this.userPaused = false;
        this.start(true);
      }
    });

    if (reducedMotion()) {
      // Nothing moves by itself; the button offers to play
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.setAttribute('aria-label', 'Play video');
      }
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible && !this.userPaused) this.start(false);
        else if (!visible) this.video.pause();
      },
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /** @param {boolean} fromUser */
  start(fromUser) {
    const { video } = this;
    if (!video.getAttribute('src')) {
      const src = isPhone() ? this.dataset.srcMobile || this.dataset.src : this.dataset.src;
      if (!src) return;
      video.src = src;
      if (this.toggle) this.toggle.hidden = false;
    }
    if (reducedMotion() && !fromUser) return;
    video.play().catch(() => {});
  }
}

if (!customElements.get('fa-bundles-loop')) customElements.define('fa-bundles-loop', FaBundlesLoop);

/* ---------------------------------------------------------------- Page-level behaviour */

if (!window.faBundlesPage) {
  window.faBundlesPage = true;

  // "Choose your set" and the other in-page links
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-fa-bundles-scroll]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    const hash = link.getAttribute('href') || '';
    if (!hash.startsWith('#') || hash.length < 2) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  // The makers film: the same start-up as the theme's video blocks (blocks/fa-video)
  if (!window.faVideoStart) {
    window.faVideoStart = true;
    document.addEventListener('click', async (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-fa-video-start]') : null;
      const player = button?.closest('fa-video-player');
      if (!button || !player) return;
      const fromKeyboard = event.detail === 0;

      await customElements.whenDefined('fa-video-player');
      button.hidden = true;
      /** @type {any} */ (player).load();

      if (fromKeyboard) player.querySelector('.fa-video-player__surface')?.focus({ preventScroll: true });
    });
  }
}
